<template>
  <div v-if="isShowModuleModal" class="modal d-block" tabindex="-1" style="background-color: rgba(0, 0, 0, 0.5);">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">{{ isEditMode ? 'Edit Tenant' : 'Create Tenant' }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeModuleModal"></button>
        </div>

        <!-- Form Content -->
        <form @submit.prevent="handleSubmit">
          <div class="modal-body">
            <!-- Row 1: Organization ID, Tenant Name, Tenant Code -->
            <div class="form-row form-row-3">
              <div class="form-group">
                <label class="form-label required">Organization ID</label>
                <input
                  v-model="formData.organizationId"
                  type="text"
                  placeholder="e.g. ORG-001"
                  class="form-input"
                  @blur="validateField('organizationId')"
                />
                <span v-if="errors.organizationId" class="error-text">
                  {{ errors.organizationId }}
                </span>
              </div>

              <div class="form-group">
                <label class="form-label required">Tenant Name</label>
                <input
                  v-model="formData.tenantName"
                  type="text"
                  placeholder="e.g. Acme Corp"
                  class="form-input"
                  @blur="validateField('tenantName')"
                />
                <span v-if="errors.tenantName" class="error-text">
                  {{ errors.tenantName }}
                </span>
              </div>

              <div class="form-group">
                <label class="form-label required">Tenant Code</label>
                <input
                  v-model="formData.tenantCode"
                  type="text"
                  placeholder="e.g. ACME"
                  class="form-input"
                  @blur="validateField('tenantCode')"
                />
                <span v-if="errors.tenantCode" class="error-text">
                  {{ errors.tenantCode }}
                </span>
              </div>
            </div>

            <!-- Row 2: URL, Auth Tenant ID, Connection String -->
            <div class="form-row form-row-3">
              <div class="form-group">
                <label class="form-label">URL</label>
                <input
                  v-model="formData.url"
                  type="text"
                  placeholder="e.g. https://acme.example.com"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Auth Tenant ID</label>
                <input
                  v-model="formData.authTenantId"
                  type="text"
                  placeholder="e.g. auth-tenant-id"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Connection String</label>
                <input
                  v-model="formData.connectionString"
                  type="text"
                  placeholder="e.g. Server=...;Database=..."
                  class="form-input"
                />
              </div>
            </div>

            <!-- Row 3: Root User Name, Root User Email, Logo URL -->
            <div class="form-row form-row-3">
              <div class="form-group">
                <label class="form-label">Root User Name</label>
                <input
                  v-model="formData.rootUserName"
                  type="text"
                  placeholder="e.g. Jane Admin"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label class="form-label required">Root User Email</label>
                <input
                  v-model="formData.rootUserEmail"
                  type="email"
                  placeholder="e.g. admin@acme.com"
                  class="form-input"
                  @blur="validateField('rootUserEmail')"
                />
                <span v-if="errors.rootUserEmail" class="error-text">
                  {{ errors.rootUserEmail }}
                </span>
              </div>

              <div class="form-group">
                <label class="form-label">Logo URL</label>
                <input
                  v-model="formData.logoUrl"
                  type="text"
                  placeholder="e.g. https://.../logo.png"
                  class="form-input"
                />
              </div>
            </div>

            <!-- Row 4: Region, Environment, License ID -->
            <div class="form-row form-row-3">
              <div class="form-group">
                <label class="form-label">Region</label>
                <input
                  v-model="formData.region"
                  type="text"
                  placeholder="e.g. us-east-1"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Environment</label>
                <input
                  v-model="formData.environment"
                  type="text"
                  placeholder="e.g. production"
                  class="form-input"
                />
              </div>

              <div class="form-group">
                <label class="form-label">License ID</label>
                <input
                  v-model="formData.licenseId"
                  type="text"
                  placeholder="e.g. LIC-12345"
                  class="form-input"
                />
              </div>
            </div>

            <!-- Welcome Text -->
            <div class="form-group">
              <label class="form-label">Welcome Text</label>
              <textarea
                v-model="formData.welcomeText"
                rows="2"
                placeholder="Message shown to users on first login"
                class="form-input form-textarea"
              ></textarea>
            </div>

            <!-- Toggles -->
            <div class="form-group toggles-group">
              <label class="form-label">Settings</label>
              <div class="toggle-row">
                <label class="toggle-item">
                  <input type="checkbox" v-model="formData.isActive" />
                  <span>Active</span>
                </label>
                <label class="toggle-item">
                  <input type="checkbox" v-model="formData.isDeleted" />
                  <span>Deleted</span>
                </label>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="modal-footer">
            <button
              type="button"
              class="button button-secondary"
              @click="closeModuleModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="button button-primary"
              :disabled="isSubmitting"
            >
              <span v-if="!isSubmitting">✓ {{ isEditMode ? 'Save Changes' : 'Create Tenant' }}</span>
              <span v-else>{{ isEditMode ? 'Saving...' : 'Creating...' }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script src="./TenantModel.js"></script>

<style scoped>
* {
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.form-row-3 {
  grid-template-columns: repeat(3, 1fr);
}

.form-label {
  display: block;
  font-size: 10px;
  font-weight: 700;
  color: #014906;
  text-transform: none;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.form-label.required::after {
  content: ' *';
  color: #ef4444;
}

.form-input,
.form-select {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  color: #1f2937;
  background-color: #ffffff;
  font-family: inherit;
  transition: all 0.2s;
}

.form-textarea {
  resize: vertical;
  min-height: 48px;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #14b8a6;
  box-shadow: 0 0 0 3px rgba(20, 184, 166, 0.1);
}

.form-input::placeholder {
  color: #9ca3af;
}

.error-text {
  display: block;
  font-size: 12px;
  color: #ef4444;
  margin-top: 6px;
}

.toggles-group .toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
  font-weight: 400;
  text-transform: none;
}

.toggle-item input[type="checkbox"] {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: #2e4a2fb8;
}

.button {
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.button-secondary {
  background-color: #ffffff;
  color: #374151;
  border: 1px solid #d1d5db;
}

.button-secondary:hover {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.button-primary {
  background-color: #797a7b;
  color: white;
}

.button-primary:hover:not(:disabled) {
  background-color: #0d9488;
  box-shadow: 0 4px 12px rgba(20, 184, 166, 0.3);
}

.button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.modal-title{
  font-size: 13px;
  font-weight: 700;
  color: #797a7b;
}
.modal-header{
  padding: 12px 18px;
}
.modal-footer{
  padding: .45rem;
}
.btn-close{
color: #6B7280;
}
/* Responsive design */
@media (max-width: 768px) {
  .form-row-3 {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 640px) {
  .form-row,
  .form-row-3 {
    grid-template-columns: 1fr;
    gap: 12px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>